import type { FC, HTMLAttributes, MouseEventHandler, ReactNode, RefAttributes } from "react";
import { ChevronDown, Share04 } from "@untitledui/icons";
import type { LinkProps as AriaLinkProps } from "react-aria-components";
import { Link as AriaLink } from "react-aria-components";
import { cx, sortCx } from "../../../../utils/cx";

const styles = sortCx({
    root: "group relative flex min-h-10 w-full cursor-pointer items-center rounded-lg bg-transparent outline-focus-ring transition duration-100 ease-linear select-none hover:bg-primary_hover focus-visible:z-10 focus-visible:outline-2 focus-visible:outline-offset-2",
    rootSelected:
        "bg-brand-primary_alt hover:bg-brand-primary_alt before:absolute before:inset-y-2 before:-start-4 before:w-1 before:rounded-e-full before:bg-fg-brand-primary dark:bg-secondary dark:hover:bg-secondary_hover",
});

/**
 * Coloured count pill for nav items (e.g. review-queue size). `error` draws attention, `brand` is calmer.
 * Numbers render with Persian tabular digits; RTL-safe (isolated).
 */
export const NavBadge = ({
    children,
    color = "error",
    className,
    "aria-label": ariaLabel,
}: {
    children: ReactNode;
    color?: "error" | "brand" | "success" | "gray";
    className?: string;
    "aria-label"?: string;
}) => (
    <span
        aria-label={ariaLabel}
        className={cx(
            "ms-3 inline-flex h-5.5 min-w-6 shrink-0 items-center justify-center rounded-full px-1.5 text-xs font-semibold digits-tabular [unicode-bidi:isolate]",
            color === "error" && "bg-error-solid text-white",
            color === "brand" && "bg-brand-solid text-white",
            color === "success" && "bg-success-solid text-white",
            color === "gray" && "bg-tertiary text-secondary",
            className,
        )}
    >
        {children}
    </span>
);

/**
 * Props shared between the collapsible and link variants
 */
interface NavItemCommonProps {
    /** Icon component to display. */
    icon?: FC<HTMLAttributes<HTMLOrSVGElement>>;
    /** Badge to display. */
    badge?: ReactNode;
    /** Whether the nav item is currently active. */
    current?: boolean;
    /** Whether to truncate the label text. */
    truncate?: boolean;
    /** `sm`: secondary items (help, account) — smaller, muted, tighter. */
    size?: "md" | "sm";
    /** Handler for click events. */
    onClick?: MouseEventHandler;
    /** Content to display. */
    children?: ReactNode;
}

/**
 * Props for the collapsible variant (renders a `summary` element)
 */
export interface NavItemCollapsibleProps extends NavItemCommonProps {
    /** Type of the nav item. */
    type: "collapsible";
}

/**
 * Props for the link variants (anchor tag). Accepts all React Aria `Link` props such as `routerOptions`, `isDisabled` and `onPress`.
 */
export interface NavItemLinkProps extends NavItemCommonProps, Omit<AriaLinkProps, "children" | "className" | "onClick">, RefAttributes<HTMLAnchorElement> {
    /** Type of the nav item. */
    type: "link" | "collapsible-child";
    /** URL to navigate to when the nav item is clicked. */
    href?: AriaLinkProps["href"];
}

/** Union type of collapsible and link props */
export type NavItemBaseProps = NavItemCollapsibleProps | NavItemLinkProps;

export const NavItemBase = (props: NavItemBaseProps) => {
    const { icon: Icon, badge, current, truncate = true, size = "md", onClick, children, ...rest } = props;
    const sm = size === "sm";

    const iconElement = Icon && (
        <Icon
            aria-hidden="true"
            className={cx(
                "shrink-0 text-fg-quaternary transition-inherit-all group-hover/item:text-fg-quaternary_hover",
                sm ? "me-2.5 size-4" : "me-3 size-5",
                current && "text-fg-brand-primary group-hover/item:text-fg-brand-primary",
            )}
        />
    );

    const badgeElement =
        badge && (typeof badge === "string" || typeof badge === "number") ? (
            <NavBadge>{badge}</NavBadge>
        ) : (
            badge
        );

    const labelElement = (
        <span
            className={cx(
                "flex-1 transition-inherit-all",
                sm
                    ? "text-[13px] font-medium text-tertiary group-hover/item:text-secondary_hover"
                    : "text-sm font-semibold text-secondary group-hover/item:text-secondary_hover",
                truncate && "truncate",
                current && "text-brand-secondary group-hover/item:text-brand-secondary dark:text-primary",
            )}
        >
            {children}
        </span>
    );

    if (rest.type === "collapsible") {
        return (
            <summary className={cx("p-2", styles.root, current && styles.rootSelected)} onClick={onClick}>
                {iconElement}

                {labelElement}

                {badgeElement}

                <ChevronDown aria-hidden="true" className="ms-3 size-4 shrink-0 stroke-[2.5px] text-fg-quaternary in-open:-scale-y-100" />
            </summary>
        );
    }

    const { type, href, isDisabled, ...linkProps } = rest;

    const isExternal = href?.startsWith("http");
    const externalIcon = isExternal && <Share04 className="size-4 stroke-[2.5px] text-fg-quaternary" />;

    return (
        <AriaLink
            // Defaults that consumers can override through React Aria link props.
            target={isExternal ? "_blank" : "_self"}
            rel="noopener noreferrer"
            aria-current={current ? "page" : undefined}
            {...linkProps}
            // Dropping `href` when disabled prevents navigation via middle-click or "open in new tab".
            href={isDisabled ? undefined : href}
            isDisabled={isDisabled}
            onClick={onClick}
            className={cx(
                type === "collapsible-child" ? "py-2 pe-3 ps-10" : sm ? "group/item px-2 py-1.5" : "group/item p-2",
                styles.root,
                sm && "min-h-8",
                current && styles.rootSelected,
                "disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
            )}
        >
            {type === "link" && iconElement}
            {labelElement}
            {externalIcon}
            {badgeElement}
        </AriaLink>
    );
};
